import romanize from 'romanize';
import { nanoid } from 'nanoid';
import render from 'dom-serializer';
import { v4 as uuidv4 } from 'uuid';
import { isElement, sortBy, cloneDeep } from 'lodash';
import { parseDocument } from 'htmlparser2';
import { Constants, Utils } from '@praktio/common';

import { isEmpty, isSpace } from './functions';
import {
  CATALayouts,
  EDIT_ANNOTATION_CLASS,
  EDIT_ANNOTATION_CLASS_PREFIX,
  MATERIAL_ICON_CLASS,
} from './constants';
import { RootState } from '../ducks/configureStore';

import {
  createWrapperAnnotationSpan,
  getSpanAnnotationsInRange,
  groupSpanAnnotationsByParent,
  mergeSpanAnnotations,
  spanAnnotationIsMarkup,
  getSubTypeClass,
  filterAnnotations,
  addWrapperAnnotationSpanGroupClass,
} from './annotations';

const { SlideVersions } = Constants;

const isWhitespace = (char: string) => {
  return ['\n', ' '].includes(char);
};

const { processAssetWithExisting } = Utils.Slide;

const {
  TemplateTypes,
  AnnotationTypes,
  ResourceTypes,
  ResourceItemTypes,
  EditorBodyId,
  ElementPositions,
  OutlineTypes,
  AdaptiveTypes,
  CataTypes,
} = Constants;

const HtmlParseLookahead: Lookahead = {
  '<': {
    stop: '>',
  },
};

const htmlGatherEntity = {
  startGather: '&',
  stopGather: ';',
};

export const htmlTextIndexed = (htmlString: string) => {
  if (htmlString) {
    let newContent = '';
    let index = 0;

    let lookahead;
    let gather;
    for (const character of htmlString) {
      if (!lookahead && character in HtmlParseLookahead) {
        lookahead = HtmlParseLookahead[character];
      }

      if (!gather && htmlGatherEntity.startGather === character) {
        gather = character;
        continue;
      }

      if (lookahead) {
        newContent += character;
        if (lookahead.stop === character) {
          lookahead = null;
        }
      } else if (gather) {
        gather += character;
        if (character === htmlGatherEntity.stopGather) {
          newContent += `<span data-id="${index++}">${gather}</span>`;
          gather = null;
        }
      } else {
        newContent += `<span data-id="${index++}">${character}</span>`;
      }
    }

    return newContent;
  }
};

export const modifyAnnotation = ({
  indexedContent,
  annotations,
  annotation,
  isDelete,
}: {
  indexedContent: string;
  annotations: Annotation[];
  annotation: Annotation;
  isDelete?: boolean;
}) => {
  if (annotation) {
    const updatedAnnotations = annotations ? cloneDeep(annotations) : [];
    let updatedContent = indexedContent;
    let offset = 0;
    let length = 0;
    const isAddition =
      annotation.subType === AnnotationTypes.Addition ||
      annotation.subType === AnnotationTypes.Addition_v2;
    const index = updatedAnnotations.findIndex((a) => a && a._id === annotation._id);
    if (index > -1) {
      if (!isDelete) {
        const updatedAnnotation = {
          ...updatedAnnotations[index],
          ...annotation,
        };

        if (isAddition) {
          const ann = updatedAnnotations[index];
          if (ann && ann.text && annotation.text) {
            length = ann.text.length;
            offset = annotation.text.length - length;
            updatedAnnotation.name = updatedAnnotation.text;
          }
        }

        updatedAnnotations[index] = updatedAnnotation;
      } else {
        updatedAnnotations.splice(index, 1);
        if (isAddition && annotation.text) {
          length = annotation.text.length;
          offset = length * -1;
        }
      }
    } else if (!isDelete) {
      let name =
        annotation.name || isAddition
          ? annotation.text
          : `${annotation.subType} ${updatedAnnotations.length + 1}`;
      if (isAddition && annotation.text) {
        length = annotation.text.length;
        offset = length;
      }

      updatedAnnotations.push({
        ...annotation,
        name,
      });
    }

    let additions: Annotation[] = [];
    if (isAddition) {
      const annotationsToDelete: Annotation[] = [];
      const endIndex = (annotation.startIndex || 0) + (length ? length - 1 : 0);
      updatedAnnotations.forEach((a) => {
        if (a && a._id !== annotation._id) {
          if (
            a.type === ResourceItemTypes.Annotation &&
            !isEmpty(a.startIndex) &&
            !isEmpty(a.endIndex) &&
            !isEmpty(annotation.startIndex) &&
            !isEmpty(annotation.endIndex)
          ) {
            let deleteStartWithin = false;
            let deleteEndWithin = false;
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            if (a.startIndex >= annotation.startIndex) {
              // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
              if (a.startIndex <= endIndex && isDelete) {
                deleteStartWithin = true;
              } else {
                // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
                a.startIndex += offset;
              }
            }
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            if (a.endIndex >= annotation.startIndex) {
              // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
              if (a.endIndex <= endIndex && isDelete) {
                deleteEndWithin = true;
              } else {
                // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
                a.endIndex += offset;
              }
            }

            if (deleteStartWithin && deleteEndWithin) {
              annotationsToDelete.push(a);
            } else if (deleteStartWithin) {
              a.startIndex = annotation.startIndex;
            } else if (deleteEndWithin) {
              // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
              a.endIndex = annotation.startIndex - 1;
            }

            if (
              a.subType === AnnotationTypes.Addition ||
              a.subType === AnnotationTypes.Addition_v2
            ) {
              additions.push(a);
            }
            /* TODO: What does this do? snippet isn't declared anywhere, or used
          else if (a.subType === AnnotationTypes.Snippet) {
            snippet = a;
          }
          */
          }
        } else if (!isDelete) {
          additions.push(a);
        }
      });

      annotationsToDelete.forEach((r) => {
        const index = updatedAnnotations.findIndex((tr) => tr && r && tr._id === r._id);
        if (index > -1) {
          updatedAnnotations.splice(index, 1);
        }
      });
    } else {
      additions = updatedAnnotations.filter(
        (a) =>
          a &&
          (a.subType === AnnotationTypes.Addition || a.subType === AnnotationTypes.Addition_v2),
      );
    }

    additions.sort((a, b) => {
      if (a && b && !isEmpty(a.startIndex) && !isEmpty(b.startIndex)) {
        // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
        if (a.startIndex < b.startIndex) {
          return -1;
          // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
        } else if (a.startIndex > b.startIndex) {
          return 1;
        }
      }
      return 0;
    });

    updatedContent = insertCharacterLevelAdditions(indexedContent, additions);

    return { indexedContent: updatedContent, annotations: updatedAnnotations };
  }
};

export const removeDeletesStripFormatting = (content: string) => {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(content, 'text/html');
  const deletes = doc.querySelectorAll('.is-annotation.Delete_v2');
  deletes.forEach((d) => d.remove());
  const spans = doc.querySelectorAll('.is-annotation');
  spans.forEach((s) => (s.className = ''));
  return htmlTextIndexed(stripSpans(doc.documentElement.outerHTML));
};

/**
 * Wraps annotated spans for accessibility purposes by grouping and wrapping relevant span elements
 * based on their annotations. This function processes a list of filtered span elements and their
 * associated annotation data, merging and filtering annotations, and then wrapping groups of spans
 * in new wrapper spans with appropriate classes for accessibility.
 *
 * - Only annotations that are markup and should be displayed live are considered.
 * - Groups spans by their parent element and wraps each group in a new span with annotation classes.
 * - Handles special cases for icon spans to avoid adding special characters.
 *
 * @param filteredSpans - Array of span elements to process and wrap.
 * @param spanAnnotationClasses - Mapping of annotation IDs to their associated CSS classes.
 * @param spanAnnotations - Array of span annotation objects containing metadata for each annotation.
 * @param slideType - Optional type of slide, used to determine display logic for annotations.
 */
const wrapAnnotatedSpansForAccessibility = (
  filteredSpans: Element[],
  spanAnnotationClasses: SpanClasses,
  spanAnnotations: SpanAnnotation[],
  slideType?: string,
) => {
  const mergedAndFilteredAnnotations = filterAnnotations(mergeSpanAnnotations(spanAnnotations));

  // Iterate through all span annotations
  mergedAndFilteredAnnotations.forEach((annotation: SpanAnnotation) => {
    if (!spanAnnotationIsMarkup(annotation) || !shouldDisplayLive(annotation, slideType)) {
      return;
    }

    const subTypeClass = getSubTypeClass(annotation);

    const spanAnnotationsInRange = getSpanAnnotationsInRange(filteredSpans, annotation);

    if (!spanAnnotationsInRange.length) {
      return;
    }

    const spanAnnotationGroups = groupSpanAnnotationsByParent(spanAnnotationsInRange);

    if (!spanAnnotationGroups) {
      return;
    }

    if (!annotation.active) {
      return;
    }

    // Loop through each group of span annotations and wrap them in a new span
    spanAnnotationGroups.forEach((group: Element[], index: number) => {
      const wrapperSpan = createWrapperAnnotationSpan(subTypeClass as string);

      addWrapperAnnotationSpanGroupClass(wrapperSpan, spanAnnotationGroups, index);

      const firstSpan = group?.[0]?.parentNode;

      if (!firstSpan) {
        return;
      }

      firstSpan.insertBefore(wrapperSpan, group[0]); // Insert wrapperSpan before the first character in the annotation range

      group.forEach((span: Element) => {
        const theId = span.getAttribute('data-id') || '';
        const classes = spanAnnotationClasses[theId];
        // In processDocument_v2, span-annotation classes are applied to the DOM after this runs,
        // so check the class map. Classes already on the DOM (v1's processor_v1, icon classes in
        // the document content, Addition_v2 containers) are caught by the closest() check in
        // prependSpecialCharToCharSpan. Entries can hold several space-joined classes
        // (e.g. 'pa-material-icons is-edit edit-pa-material-icons').
        const isIcon = classes?.some((c) => c.split(' ').includes(MATERIAL_ICON_CLASS));

        // Prevent adding special characters to character spans that make up an icon
        if (isIcon) {
          return wrapperSpan.appendChild(span);
        }

        return wrapperSpan.appendChild(prependSpecialCharToCharSpan(span as HTMLSpanElement));
      }); // Append all spans in group to wrapperSpan
    });
  });
};

const childIsEmsp = (parent: DomNodeWithChildren): boolean => {
  if (parent && parent.children && parent.children.length) {
    let isEmsp = true;
    parent.children.forEach((c) => {
      const child = c as DomNodeWithChildren;
      if (
        child.type !== 'text' ||
        (child.type === 'text' && child.data !== '&emsp;' && child.data !== '\u2003')
      ) {
        isEmsp = false;
      }
    });
    return isEmsp;
  }

  return false;
};

const childIsEmsp_Node = (parent: Node): boolean => {
  if (parent && parent.childNodes && parent.childNodes.length) {
    let isEmsp = true;
    parent.childNodes.forEach((c) => {
      if (
        c.nodeType !== Node.TEXT_NODE ||
        (c.nodeType === Node.TEXT_NODE && c.textContent !== '&emsp;' && c.textContent !== '\u2003')
      ) {
        isEmsp = false;
      }
    });
    return isEmsp;
  }

  return false;
};

const addAnnotationToNode = (
  node: DomNodeWithChildren,
  spanAnnotationClasses: SpanClasses,
  tooltipText: TooltipText,
  isLive: boolean,
  hasSnippet: boolean,
  showParagraphMarks: boolean,
) => {
  if (node.type === 'tag' && node.name === 'span' && node.attribs && node.attribs['data-id']) {
    const spanIndex = node.attribs['data-id'];
    const classes = spanAnnotationClasses[spanIndex];
    if (classes) {
      // Filter out Snippet styling if live
      const filteredClasses = classes
        .filter((a) => (isLive ? a !== AnnotationTypes.Snippet : a))
        .join(' ');
      if (!node.attribs['class']) {
        node.attribs.class = filteredClasses;
      } else {
        node.attribs.class += ' ' + filteredClasses;
      }

      if (isLive && classes.includes(AnnotationTypes.Tooltip)) {
        node.attribs['data-tippy-content'] = tooltipText[spanIndex];
      }
    }

    if (isLive && node.children && node.children.length) {
      node.children.forEach((child) => {
        const c = child as DomNodeWithChildren;
        if (c.type === 'text') {
          if (showParagraphMarks) {
            switch (c.data) {
              // space replaced with centered black dot
              case '\u0020':
              case ' ':
                c.data = '∙\u200B';
                break;
              // non-breaking space replaced with top-aligned empty dot
              case '\u00A0':
              case '&nbsp;':
                c.data = '\u00B0';
                break;
              default:
                break;
            }

            // three emsp's in a row is a tab, so if one is encountered, check the previous two nodes
            if (c.data === '&emsp;' || c.data === '\u2003') {
              if (c.parent && c.parent.prev && c.parent.prev.prev) {
                if (
                  childIsEmsp(c.parent.prev as DomNodeWithChildren) &&
                  childIsEmsp(c.parent.prev.prev as DomNodeWithChildren)
                ) {
                  ((c.parent.prev as DomNodeWithChildren).children[0] as DomNodeWithChildren).data =
                    '\u2192';
                }
              }
            }
          }
        }
      });
    }
  }

  return node;
};

const processNodes = (
  nodes: DomNodeWithChildren[],
  transform: Function,
  spanAnnotationClasses: SpanClasses,
  tooltipText: TooltipText,
  isLive: boolean,
  hasSnippet: boolean,
  liAncestor: boolean,
  showParagraphMarks: boolean,
  selected: unknown,
  slideType: string,
) => {
  nodes.forEach((n, i) => {
    if (n.children) {
      processNodes(
        n.children as DomNodeWithChildren[],
        transform,
        spanAnnotationClasses,
        tooltipText,
        isLive,
        hasSnippet,
        n.name === 'li' || liAncestor,
        showParagraphMarks,
        selected,
        slideType,
      );
    }

    transform(
      n,
      spanAnnotationClasses,
      tooltipText,
      isLive,
      hasSnippet,
      showParagraphMarks,
      selected,
    );

    // Check to see if the element is an 'li'
    // or has a 'li' ancestor, but is not a span with data-id
    // If so, check all the children to see if they are hidden
    // and if they are, hide the element
    if (
      n.children &&
      isLive &&
      (liAncestor || n.name === 'li') &&
      !(n.name === 'span' && n.attribs && n.attribs['data-id'])
    ) {
      const hasVisibleChild = n.children.find((child) => {
        const c = child as DomNodeWithChildren;
        const displayNone = c?.attribs?.class?.includes('d-none');
        if (c.name === 'span') {
          if (c.children?.length === 1) {
            const data = c.children[0].data;
            const whitespace = isWhitespace(data);
            return !whitespace && data && !displayNone;
          } else if (!c.children || c.children.length === 0) {
            return false;
          }
        }

        return c.type === 'tag' && !displayNone;
      });

      const hasInFocusChild = n.children.find((child) => {
        const c = child as DomNodeWithChildren;
        const outFocus = c?.attribs?.class?.includes('out-focus');
        if (c.name === 'span') {
          if (c.children?.length === 1) {
            const data = c.children[0].data;
            const whitespace = isWhitespace(data);
            return !whitespace && data && !outFocus;
          } else if (!c.children || c.children.length === 0) {
            return false;
          }
        }

        return c.type === 'tag' && !outFocus;
      });

      if (!n.attribs) {
        n.attribs = {
          class: '',
        };
      }

      let className = n.attribs.class || '';
      if (!hasVisibleChild) {
        if (!className.includes('d-none')) {
          className += ' d-none';
        }
      } else {
        className = className.replace('d-none', '');
        if (!hasInFocusChild) {
          className += ' out-focus';
        } else {
          className = className.replace('out-focus', '');
        }
      }

      n.attribs.class = className;
    }

    const dataId = n.attribs ? n.attribs['data-id'] : null;
    if (isLive && slideType === TemplateTypes.ClickBestSpot && dataId) {
      const hashed = hashId(dataId);
      n.attribs['data-id'] = hashed;
      if (selected === hashed) {
        if (!n.attribs['class']) {
          n.attribs.class = 'active';
        } else {
          n.attribs.class += ' active';
        }
      }
    }
  });
};

const shouldDisplayLive = (
  annotation: Annotation,
  slideType?: string,
  outOfTries?: boolean,
): boolean => {
  if (annotation.subType === AnnotationTypes.Snippet) return false;
  return (
    ((outOfTries ? !annotation.hideForFeedback : !annotation.hideForQuiz) &&
      (annotation.active ||
        annotation.subType === AnnotationTypes.Delete ||
        annotation.subType === AnnotationTypes.Focus ||
        annotation.subType === AnnotationTypes.Addition ||
        annotation.subType === AnnotationTypes.Addition_v2 ||
        (annotation.subType === AnnotationTypes.Strikethrough &&
          annotation.hideAfter &&
          annotation.pastEnd) ||
        (annotation.subType === AnnotationTypes.Delete_v2 &&
          annotation.showStyling &&
          annotation.hideAfter &&
          annotation.pastEnd))) ||
    false
  );
};

const HIDDEN_ANNOTATION_CLASS = 'd-none';

const getBaseAnnotationClass = (
  isLive: boolean | undefined,
  annotation: Annotation,
  slideType?: string,
) => {
  if (isLive) {
    if (
      (annotation.subType === AnnotationTypes.Strikethrough &&
        annotation.hideAfter &&
        annotation.pastEnd) ||
      (annotation.subType === AnnotationTypes.Focus && !annotation.active && !annotation.pastEnd) ||
      annotation.subType === AnnotationTypes.Delete ||
      (slideType === TemplateTypes.Explanation &&
        (annotation.subType === AnnotationTypes.Addition ||
          annotation.subType === AnnotationTypes.Addition_v2) &&
        ((!annotation.active && !annotation.pastEnd) ||
          (annotation.pastEnd && annotation.hideAfter))) ||
      (annotation.subType === AnnotationTypes.Delete_v2 &&
        (!annotation.showStyling ||
          (annotation.showStyling && annotation.hideAfter && annotation.pastEnd)))
    ) {
      return HIDDEN_ANNOTATION_CLASS;
    } else if (annotation.subType === AnnotationTypes.Focus) {
      return annotation.pastEnd ? 'out-focus' : 'in-focus';
    } else if (
      slideType === TemplateTypes.Explanation &&
      (annotation.subType === AnnotationTypes.Addition ||
        annotation.subType === AnnotationTypes.Addition_v2) &&
      !annotation.active &&
      annotation.pastEnd
    ) {
      return;
    } else if (annotation.subType === AnnotationTypes.Addition_v2 && !annotation.showStyling) {
      return annotation.includeHighlight ? 'Highlight' : null;
    } else if (
      slideType === TemplateTypes.ClickBestSpot &&
      annotation.subType === AnnotationTypes.Addition
    ) {
      return;
    }
  }

  return `${
    annotation.className ||
    (annotation.subType === AnnotationTypes.Dynamic && annotation.styleClass) ||
    annotation.subType
  }${annotation.includeHighlight ? ' Highlight' : ''}`;
};

// An Answer's own edits render with the same subtype class as a main-timeline
// annotation, plus a flat marker and one `edit-<class>` per class. Classes accumulate
// per character span from every annotation covering it, so the per-class form is what
// lets a stylesheet target the edit without also matching a sibling annotation that
// happens to cover the same characters.
const getAnnotationClass = (
  isLive: boolean | undefined,
  annotation: Annotation,
  slideType?: string,
) => {
  const annotationClass = getBaseAnnotationClass(isLive, annotation, slideType);
  if (!annotation.isAnswerEdit || !annotationClass || annotationClass === HIDDEN_ANNOTATION_CLASS) {
    return annotationClass;
  }

  const editClasses = annotationClass
    .split(' ')
    .filter(Boolean)
    .map((c) => `${EDIT_ANNOTATION_CLASS_PREFIX}${c}`);
  return [annotationClass, EDIT_ANNOTATION_CLASS, ...editClasses].join(' ');
};

const processor_v1 = (
  content: string,
  spanAnnotationClasses: SpanClasses,
  tooltipText: TooltipText,
  isLive: boolean,
  hasSnippet: boolean,
  showParagraphMarks: boolean,
  selected: unknown,
  slideType: string,
) => {
  const document = parseDocument(content);
  processNodes(
    [document as DomNodeWithChildren],
    addAnnotationToNode,
    spanAnnotationClasses,
    tooltipText,
    isLive,
    hasSnippet,
    false,
    showParagraphMarks,
    selected,
    slideType,
  );

  let nodesToUse = document;
  let node = nodesToUse as DomNodeWithChildren;
  if (node.type === 'tag' && node.name === 'html') {
    node = (node.children as DomNodeWithChildren[]).find(
      (n) => n.type === 'tag' && n.name === 'body',
    ) as DomNodeWithChildren;
    if (node && node.children) {
      nodesToUse = node.children as any;
    }
  }

  return render(nodesToUse as any);
};

const addAnnotationsToContent_generic = (
  processor: Function,
  content: string,
  annotations: AnnotationOrNone[],
  slideType?: string,
  isLive?: boolean,
  showParagraphMarks?: boolean,
  selected?: unknown,
) => {
  if (!annotations) {
    return content;
  }

  const spanAnnotationClasses: SpanClasses = {};
  const tooltipText: TooltipText = {};
  let hasSnippet = false;
  const hasSelections =
    annotations.find((a) => a && a.type === ResourceItemTypes.Annotation && a.selected) && !isLive;

  const spanAnnotations: SpanAnnotation[] = [];
  const elementAnnotations: Annotation[] = [];
  annotations.forEach((a) => {
    if (a) {
      let visible = true;
      if (hasSelections) {
        visible =
          a.type !== ResourceItemTypes.Annotation ||
          (a.type === ResourceItemTypes.Annotation &&
            (a.selected || a.subType === AnnotationTypes.Snippet));
      }

      if (visible) {
        if (isBaseAnnotation(a) || isLayeredAnnotation(a)) {
          spanAnnotations.push(a as SpanAnnotation);
        } else {
          elementAnnotations.push(a);
        }
      }
    }
  });

  spanAnnotations
    .sort((a, b) => {
      if (
        ((a.subType === AnnotationTypes.Addition || a.subType === AnnotationTypes.Addition_v2) &&
          b.subType !== AnnotationTypes.Addition &&
          b.subType !== AnnotationTypes.Addition_v2) ||
        a.startIndex < b.startIndex
      ) {
        return -1;
      } else if (
        (a.subType !== AnnotationTypes.Addition &&
          a.subType !== AnnotationTypes.Addition_v2 &&
          (b.subType === AnnotationTypes.Addition || b.subType === AnnotationTypes.Addition_v2)) ||
        a.startIndex > b.startIndex
      ) {
        return 1;
      }
      return 0;
    })
    .forEach((a) => {
      if (
        a.type === ResourceItemTypes.Annotation &&
        (a.startIndex || a.startIndex === 0) &&
        (a.endIndex ||
          a.subType === AnnotationTypes.Addition ||
          a.subType === AnnotationTypes.Addition_v2)
      ) {
        let startIndex = a.startIndex;
        let endIndex = a.endIndex;
        if (a.subType === AnnotationTypes.Snippet) {
          hasSnippet = true;
        }

        for (let i = startIndex; i <= endIndex; i++) {
          const key = (isLayeredAnnotation(a) ? `${a.rootId}-${i}` : i).toString();
          if (!spanAnnotationClasses[key]) {
            spanAnnotationClasses[key] = [];
          }
          const classes = [];
          // If live, don't process annotations as those are done after this
          // loop
          if (!isLive || shouldDisplayLive(a, slideType)) {
            const annotationClass = getAnnotationClass(isLive, a, slideType);
            if (annotationClass && annotationClass !== '') {
              if (annotationClass !== 'd-none') {
                classes.push('is-annotation');
              }
              classes.push(annotationClass);
            }
          }

          spanAnnotationClasses[key].push(...classes);
          if (a.subType === AnnotationTypes.Tooltip) {
            tooltipText[key] = a.text || '';
          }
        }
      }
    });

  const updatedContent = processor(
    content,
    spanAnnotationClasses,
    tooltipText,
    isLive,
    hasSnippet,
    showParagraphMarks,
    slideType === TemplateTypes.ClickBestSpot ? selected : null,
    slideType,
  );

  const parser = new DOMParser();
  const doc = parser.parseFromString(updatedContent, 'text/html');
  const keys = Object.keys(spanAnnotationClasses);
  const spanIdsSelector = 'span[data-id]'; //keys.map((k) => `span[data-id="${k}"]`).join(',');
  const spans = doc.querySelectorAll(spanIdsSelector);

  if (keys.length) {
    const filteredSpans: Element[] = [];

    spans.forEach((s) => {
      const key = s.getAttribute('data-id');
      if (key && spanAnnotationClasses[key]) {
        filteredSpans.push(s);
      }
    });

    wrapAnnotatedSpansForAccessibility(
      filteredSpans,
      spanAnnotationClasses,
      spanAnnotations,
      slideType,
    );

    elementAnnotations.forEach((a) => {
      const id = a.subType === AnnotationTypes.Addition_v2 ? a.elementId : a.rootId;
      if (id) {
        const el = doc.getElementById(id);
        if (el) {
          if (!isLive || shouldDisplayLive(a, slideType)) {
            const annotationClass = getAnnotationClass(isLive, a, slideType);
            if (annotationClass && annotationClass !== '') {
              if (annotationClass !== 'd-none') {
                el.classList.add('is-annotation');
              }

              el.classList.add(...annotationClass.split(' '));
            }
          }
        }
      }
    });
  }

  if (doc.body.textContent?.trim().length === 0) {
    return '';
  }

  return doc.body.innerHTML;
};

export const addAnnotationsToContent = (
  content: string,
  annotations: Annotation[],
  slideType?: string,
  isLive?: boolean,
  showParagraphMarks?: boolean,
  selected?: unknown,
) => {
  return addAnnotationsToContent_generic(
    processor_v1,
    content,
    annotations,
    slideType,
    isLive,
    showParagraphMarks,
    selected,
  );
};

/**
 * Prepend zero-width non-joiner (&zwnj;) or zero-width-space (&zwsp;) to the
 * text content of individual character `<span>` elements in CBS slides to ensure
 * proper screen reader formatting announcements.
 * @param span - The character span element to be updated.
 * @returns The updated character span element.
 * @example
 * Before: <span>w</span> or <span> </span>
 * After:  <span>&zwnj;A</span> or <span>&zwsp; </span>
 */
const prependSpecialCharToCharSpan = (span: HTMLSpanElement) => {
  const isNotSpan = !isElement(span) || span.tagName.toLowerCase() !== 'span';

  // If not span element or has more/less than one character, return the span as is
  if (isNotSpan || span.textContent?.length !== 1) {
    return span;
  }

  // A special character between icon characters breaks the icon font ligature.
  // Catches icon classes already on the DOM (content-embedded icons, v1, Addition_v2
  // containers, the Click Best Spot pass); span-annotation classes on the v2 path are
  // checked from the class map by wrapAnnotatedSpansForAccessibility.
  if (span.closest(`.${MATERIAL_ICON_CLASS}`)) {
    return span;
  }

  // Add a special zero-width-space character to the beginning of space characters
  // This ensures the space remains selectable and allows space runs
  // (e.g., consecutive spaces) to be announced by screen readers.
  if (isSpace(span.textContent)) {
    span.textContent = `\u200B${span.textContent}`;
    return span;
  }

  span.textContent = `\u200C${span.textContent}`;

  return span;
};

/**
 * Prepends special characters to character spans within a document.
 *
 * This function processes the provided document, finds all relevant character spans,
 * and prepends the necessary special characters to them.
 *
 * @param doc - The document element containing character spans to be updated.
 */
const prependSpecialCharToDocSpans = (doc: Document) => {
  const spans = doc.querySelectorAll('span[data-id]');

  for (let span of spans) {
    prependSpecialCharToCharSpan(span as HTMLSpanElement);
  }

  return doc;
};

export const processDocument_v2 = ({
  content,
  annotations,
  slideType,
  isLive,
  showParagraphMarks,
  selected,
  paragraphSelection,
  outOfTries,
}: {
  content: string;
  annotations: Annotation[];
  slideType?: string;
  isLive?: boolean;
  showParagraphMarks?: boolean;
  selected?: unknown;
  paragraphSelection?: boolean;
  outOfTries?: boolean;
}) => {
  if (!annotations) {
    return content;
  }

  const spanAnnotationClasses: SpanClasses = {};
  const tooltipText: TooltipText = {};
  const hasSelections =
    annotations.find((a) => a && a.type === ResourceItemTypes.Annotation && a.selected) && !isLive;

  const spanAnnotations: SpanAnnotation[] = [];
  const elementAnnotations: Annotation[] = [];
  annotations.forEach((a) => {
    let visible = true;
    if (hasSelections) {
      visible =
        a.type !== ResourceItemTypes.Annotation ||
        (a.type === ResourceItemTypes.Annotation &&
          (a.selected || a.subType === AnnotationTypes.Snippet));
    }

    if (visible) {
      if (
        (isBaseAnnotation(a) || isLayeredAnnotation(a)) &&
        (!isLive || (isLive && a.subType !== AnnotationTypes.Snippet))
      ) {
        spanAnnotations.push(a as SpanAnnotation);
      } else {
        elementAnnotations.push(a);
      }
    }
  });

  spanAnnotations
    .sort((a, b) => {
      if (
        ((a.subType === AnnotationTypes.Addition || a.subType === AnnotationTypes.Addition_v2) &&
          b.subType !== AnnotationTypes.Addition &&
          b.subType !== AnnotationTypes.Addition_v2) ||
        a.startIndex < b.startIndex
      ) {
        return -1;
      } else if (
        (a.subType !== AnnotationTypes.Addition &&
          a.subType !== AnnotationTypes.Addition_v2 &&
          (b.subType === AnnotationTypes.Addition || b.subType === AnnotationTypes.Addition_v2)) ||
        a.startIndex > b.startIndex
      ) {
        return 1;
      }
      return 0;
    })
    .forEach((a) => {
      if (
        a.type === ResourceItemTypes.Annotation &&
        (a.startIndex || a.startIndex === 0) &&
        (a.endIndex ||
          a.subType === AnnotationTypes.Addition ||
          a.subType === AnnotationTypes.Addition_v2)
      ) {
        let startIndex = a.startIndex;
        let endIndex = a.endIndex;
        for (let i = startIndex; i <= endIndex; i++) {
          const key = isLayeredAnnotation(a) ? `${a.rootId}-${i}` : `${i}`;
          if (!spanAnnotationClasses[key]) {
            spanAnnotationClasses[key] = [];
          }
          const classes = [];
          // If live, don't process annotations as those are done after this
          // loop
          if (!isLive || shouldDisplayLive(a, slideType, outOfTries)) {
            const annotationClass = getAnnotationClass(isLive, a, slideType);
            if (annotationClass && annotationClass !== '') {
              if (annotationClass !== 'd-none') {
                classes.push('is-annotation');
              }
              classes.push(annotationClass);
            }
          }

          spanAnnotationClasses[key].push(...classes);
          if (a.subType === AnnotationTypes.Tooltip) {
            tooltipText[key] = a.text || '';
          }
        }
      }
    });

  const parser = new DOMParser();
  let doc = parser.parseFromString(content, 'text/html');
  const keys = Object.keys(spanAnnotationClasses);
  const spanIdsSelector = 'span[data-id]'; //keys.map((k) => `span[data-id="${k}"]`).join(',');
  const spans = doc.querySelectorAll(spanIdsSelector);

  if (keys.length) {
    const filteredSpans: Element[] = [];

    spans.forEach((s) => {
      const key = s.getAttribute('data-id');
      if (key && spanAnnotationClasses[key]) {
        filteredSpans.push(s);
      }
    });

    wrapAnnotatedSpansForAccessibility(
      filteredSpans,
      spanAnnotationClasses,
      spanAnnotations,
      slideType,
    );

    /*** Processing Start */
    filteredSpans.forEach((s) => {
      const dataId = s.getAttribute('data-id');
      if (dataId) {
        const classes = spanAnnotationClasses[dataId];
        if (classes) {
          // Filter out Snippet styling if live
          classes.forEach((a) => {
            const splitClasses = a.split(' ');
            splitClasses.forEach((c) => {
              if (!isLive || (isLive && a !== AnnotationTypes.Snippet)) {
                s.classList.add(c);
              }
            });
          });

          if (isLive && classes.includes(AnnotationTypes.Tooltip)) {
            s.setAttribute('data-tippy-content', tooltipText[dataId]);
          }
        }
      }
    });
  }

  if (isLive && showParagraphMarks) {
    spans.forEach((s) => {
      if (s.childNodes && s.childNodes.length) {
        s.childNodes.forEach((c) => {
          if (c.nodeType === Node.TEXT_NODE) {
            if (showParagraphMarks) {
              switch (c.textContent) {
                // space replaced with centered black dot
                case '\u0020':
                case ' ':
                  c.textContent = '∙\u200B';
                  break;
                // non-breaking space replaced with top-aligned empty dot
                case '\u00A0':
                case '&nbsp;':
                  c.textContent = '\u00B0';
                  break;
                default:
                  break;
              }

              // three emsp's in a row is a tab, so if one is encountered, check the previous two nodes
              if (c.textContent === '&emsp;' || c.textContent === '\u2003') {
                if (
                  c.parentNode &&
                  c.parentNode.previousSibling &&
                  c.parentNode.previousSibling.previousSibling
                ) {
                  if (
                    childIsEmsp_Node(c.parentNode.previousSibling) &&
                    childIsEmsp_Node(c.parentNode.previousSibling.previousSibling)
                  ) {
                    c.parentNode.previousSibling.childNodes[0].textContent = '\u2192';
                  }
                }
              }
            } else if (c.textContent === ' ' || c.textContent === '\u0020') {
              c.textContent = '\u2008';
            }
          }
        });
      }
    });
  }
  /*** Processing End */
  const unmarkedAdjustments: Adjustment[] = [];
  const markedAdjustments: Adjustment[] = [];
  const deleteIds: { [index: string]: boolean } = {};
  elementAnnotations.forEach((a) => {
    const el = doc.getElementById(
      (a.subType === AnnotationTypes.Addition_v2 ? a.elementId : a.rootId) || '',
    );
    if (el) {
      if (!isLive || shouldDisplayLive(a, slideType, outOfTries)) {
        const annotationClass = getAnnotationClass(isLive, a, slideType);
        if (annotationClass && annotationClass !== '') {
          if (annotationClass !== 'd-none') {
            el.classList.add('is-annotation');
          }

          el.classList.add(...annotationClass.split(' '));
        }
        if (
          (a.subType === AnnotationTypes.Addition_v2 || a.subType === AnnotationTypes.Delete_v2) &&
          (el.classList.contains('pa-contract-article') ||
            el.classList.contains('pa-contract-numsection'))
        ) {
          // Determine what kind of adjustment it is
          // (marked vs. unmarked), and add to associated array
          const { subType, showStyling, hideAfter, active } = a;
          const isDelete = subType === AnnotationTypes.Delete_v2;
          const showMarkup =
            !showStyling ||
            (isLive && !isDelete && showStyling && !active) ||
            (isLive && isDelete && showStyling && hideAfter && !active)
              ? false
              : true;

          // TODO: PA-1503 was created because an issue was discovered where an element-level
          // Addition_v2 that had a non-zero start range, hideAfter=false was causing
          // subsequent numbering to be bumped up prior to it being active in the slide.
          // So if we have 1, 2, 3 then added Addition_v2 between 1 and 2 with a time span
          // of 2-10 seconds, then the content was showing up as 1, 3, 4 even before the
          // Addition_v2 comes into view. This is because hideAfter is not set which would
          // set decrease (variable below) to true. We need to figure out how to account for
          // that situation (difficult here because active is false before and after the time span)
          // but we want decrease=true before the time span, but decrease=false after.
          const decrease = isDelete || (isLive && !isDelete && hideAfter && !active);
          const adjustment: Adjustment = {
            annotation: a,
            el,
            decrease,
            showMarkup,
            num: 0,
          };
          if (showMarkup) {
            const numEl = el.querySelector('.pa-num') as El;
            if (numEl) {
              adjustment.num = parseInt(numEl.dataset.num, 10);
            }

            markedAdjustments.push(adjustment);
          } else {
            unmarkedAdjustments.push(adjustment);
          }

          // Track deletes for marked annotations so we don't
          // adjust them later in the process (numbers should stay the same)
          if (isDelete && a.rootId) {
            deleteIds[a.rootId] = true;
          }
        }
      }
    }
  });

  // Process unmarked adjustments first
  // in order to properly adjust non-annotated numbers
  unmarkedAdjustments.forEach((a) => {
    const { el, decrease, showMarkup, annotation } = a;
    adjustNumbering(doc, el, decrease, showMarkup);
    if (isLive && annotation.subType === AnnotationTypes.Delete_v2 && !annotation.showStyling) {
      el.remove();
    }
  });

  // Process marked adjustments, with deleteIds provided
  markedAdjustments.forEach((a) => {
    const { el, decrease, showMarkup } = a;
    adjustNumbering(doc, el, decrease, showMarkup, deleteIds);
  });

  const keysToRemove: { [key: string]: boolean } = {};
  if (isLive) {
    const targetedAdditions =
      annotations.filter((a) =>
        a.subType === AnnotationTypes.Addition_v2 && outOfTries ? a.hideForFeedback : a.hideForQuiz,
      ) || [];

    targetedAdditions.forEach((a) => {
      const layeredAnnotation = isLayeredAnnotation(a);
      const { startIndex, text, rootId } = a;
      if (!isEmpty(startIndex) && text) {
        // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
        const endIndex = startIndex + text.length - 1;
        for (let i = startIndex || 0; i <= endIndex; i++) {
          const key = layeredAnnotation ? `${rootId}-${i}` : i;
          keysToRemove[key] = true;
        }
      }
    });
  }

  if (slideType !== TemplateTypes.ClickBestSpot || paragraphSelection) {
    stripSpans_DOM(doc, true, isLive, keysToRemove);
  } else if (isLive) {
    stripAnnotationClasses_DOM(doc, keysToRemove);
  }

  if (selected || selected === 0) {
    const selector = paragraphSelection ? `#${selected}` : `span[data-id="${selected}"]`;
    const el = doc.querySelector(selector);
    if (el) {
      el.classList.add('active');
    }
  }

  if (doc.body.textContent?.trim().length === 0) {
    return '';
  }

  if (slideType === TemplateTypes.ClickBestSpot) {
    doc = prependSpecialCharToDocSpans(doc);
  }

  return doc.body.innerHTML;
};

const adjustNumbering = (
  doc: Document,
  el: Element,
  decrease: boolean | undefined,
  showMarkup: boolean,
  deleteIds?: { [index: string]: boolean },
) => {
  let num = el.querySelector('.pa-num');
  if (num) {
    let node = el.nextSibling as ChdNode;
    while (node) {
      num = node.querySelector('.pa-num');
      if (num) {
        // If a delete id exists, ignore the processing of that node
        // because we don't want to mark it/adjust numbering
        const ignore = deleteIds && deleteIds[node.id];
        if (!ignore) {
          updateOutline({
            doc,
            el: num as El,
            decrease,
            showMarkup,
          });
        }
      }
      node = node.nextSibling as ChdNode;
    }
  }
};

export const cleanContent = (content: string) =>
  content
    ? content
        .replace('<!DOCTYPE html>', '')
        .replace(/<html.*?>/g, '')
        .replace(/<head.*?>.*<\/head>/g, '')
        .replace('<head>', '')
        .replace('</head>', '')
        .replace('<body>', '')
        .replace('</body>', '')
        .replace('<html>', '')
        .replace('</html>', '')
    : '';

const checkClass = (node: Element, className: string) => {
  return node && node.classList && node.classList.contains(className);
};

export const extractSnippetContent = (
  startIndex: number,
  endIndex: number,
  indexedContent: string,
) => {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(indexedContent, 'text/html');
  return extractSnippetContent_DOM(doc, startIndex, endIndex);
};

export const extractSnippetContent_DOM = (doc: Document, startIndex: number, endIndex: number) => {
  let startSpan = doc.querySelector(`span[data-id="${startIndex}"]`);
  const endSpan = doc.querySelector(`span[data-id="${endIndex}"]`);

  // Check to see if pa-num element should be included
  if (startSpan) {
    let el: Element = startSpan;
    while (
      el.id !== EditorBodyId &&
      !checkClass(el, 'pa-contract-article') &&
      !checkClass(el, 'pa-contract-numsection') &&
      (!el.previousSibling || checkClass(el.previousSibling as Element, 'pa-num'))
    ) {
      el = el.parentNode as Element;
    }

    if (checkClass(el, 'pa-contract-article') || checkClass(el, 'pa-contract-numsection')) {
      startSpan = el;
    }
  }

  iterateAndRemoveChildren(startSpan, 'firstChild');
  iterateAndRemoveChildren(endSpan, 'lastChild');
  return doc.body.innerHTML;
};

const iterateAndRemoveChildren = (element: Element | null, childLocation: string) => {
  if (element) {
    let currentElement: Element = element;
    let currentParent: El = currentElement.parentNode as El;
    while (currentParent) {
      // Iterate through all children (based on childLocation)
      // of currentParent and remove until
      // we get to currentElement
      let child = currentParent[childLocation] as El;
      while (child !== currentElement) {
        currentParent.removeChild(child);
        child = currentParent[childLocation] as El;
      }

      currentElement = currentParent;
      currentParent = currentElement.parentNode as El;
    }
  }
};

export const getContentForAnnotation = (annotation: Annotation, indexedContent: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(indexedContent, 'text/html');
  return getContentForAnnotation_DOM(doc, annotation);
};

export const stripSpans = (
  indexedContent: string,
  preserveAnnotated?: boolean,
  isLive?: boolean,
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(indexedContent, 'text/html');
  stripSpans_DOM(doc, preserveAnnotated, isLive);
  return doc.body.innerHTML;
};

export const insertCharacterLevelAdditions = (
  indexedContent: string,
  additions: Annotation[],
  forElement?: boolean,
): string => {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(indexedContent, 'text/html');
  insertCharacterLevelAdditions_DOM(doc, additions, forElement);
  return doc.body.innerHTML;
};

export const insertElementLevelAdditions = (
  indexedContent: string,
  additions: Annotation[],
  slideVersion = SlideVersions.Base,
) => {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(indexedContent, 'text/html');
  insertElementLevelAdditions_DOM(doc, additions, slideVersion);
  return doc.body.innerHTML;
};

export const getContentForAnnotation_DOM = (doc: Document, annotation: Annotation) => {
  const { startIndex, endIndex, rootId } = annotation || {};
  const layeredAnnotation = isLayeredAnnotation(annotation);
  if (isBaseAnnotation(annotation) || layeredAnnotation) {
    const keys: { [index: string]: boolean } = {};
    if (!isEmpty(startIndex) && !isEmpty(endIndex)) {
      // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
      for (let i = startIndex || 0; i <= endIndex; i++) {
        const key = layeredAnnotation ? `${rootId}-${i}` : i;
        keys[key] = true;
      }
    }

    const spans = doc.querySelectorAll('span[data-id]');
    const filteredSpans: Element[] = [];
    spans.forEach((s) => {
      const key = s.getAttribute('data-id');
      if (key && keys[key]) {
        filteredSpans.push(s);
      }
    });

    return filteredSpans.map((s) => s.firstChild?.textContent).join('');
  } else if (rootId) {
    const el = doc.getElementById(rootId);
    if (el) {
      return el.textContent;
    }
  }

  return '';
};

export const stripSpans_DOM = (
  doc: Document,
  preserveAnnotated?: boolean,
  isLive?: boolean,
  keysToRemove?: { [key: string]: boolean },
) => {
  Object.keys(keysToRemove || {}).forEach((k) => {
    const span = doc.querySelector(`span[data-id="${k}"]`);
    if (span) {
      span.remove();
    }
  });

  let selector = `span[data-id]`;
  if (preserveAnnotated) {
    selector = `span[data-id]:not([class])${
      isLive ? ' , span[data-id][class="is-annotation Snippet"]' : ''
    }`;
  }

  const spans = doc.querySelectorAll(selector);
  for (let i = 0; i < spans.length; i++) {
    const span = spans[i];
    const parent = span.parentNode as Element;
    if (span.firstChild) {
      parent.replaceChild(span.firstChild, span);
    }
  }
};

export const stripAnnotationClasses_DOM = (
  doc: Document,
  keysToRemove?: { [key: string]: boolean },
) => {
  const selector = `span[data-id]`;
  const spans = doc.querySelectorAll(selector);
  for (let i = 0; i < spans.length; i++) {
    const span = spans[i];
    const id = span.getAttribute('data-id');
    if (span.classList.contains('d-none') || (keysToRemove && id && keysToRemove[id])) {
      span.remove();
    }

    if (id) {
      const hashedId = hashId(id);
      span.setAttribute('data-id', hashedId);
    }
  }
};

export const insertCharacterLevelAdditions_DOM = (
  doc: Document,
  additions: Annotation[],
  forElement?: boolean,
) => {
  additions
    .filter((a) => {
      if (forElement) {
        return isLayeredAnnotation(a);
      }

      return isBaseAnnotation(a);
    })
    .forEach((a) => {
      const { startIndex, text, rootId } = a;
      if (!isEmpty(startIndex)) {
        const id = startIndex || 0;
        const key = forElement ? `${rootId}-${id}` : id;
        if (key || key === 0) {
          const node = doc.querySelector(`span[data-id="${key}"]`);
          if (node && text) {
            const offset = text.length;
            const spanSelector = forElement ? `span[data-id^="${rootId}-"]` : `span[data-id]`;
            const spans = doc.querySelectorAll(spanSelector);
            spans.forEach((span) => {
              const s = span as El;
              const spanId = s.dataset.id;
              const index = parseInt(forElement ? spanId.replace(`${rootId}-`, '') : spanId, 10);
              if (index >= id) {
                s.dataset.id = (forElement ? `${rootId}-` : '') + (index + offset);
              }
            });

            const parentNode = node.parentNode as El;
            Array.from(text).forEach((char, i) => {
              const charNode = doc.createElement('span');
              charNode.dataset.id = (forElement ? `${rootId}-` : '') + (id + i);
              charNode.appendChild(doc.createTextNode(char));
              parentNode.insertBefore(charNode, node);
            });
          }
        }
      }
    });
};

const CONTENT_ELEMENT_ID_PREFIX = 's-';
const ANNOTATION_ELEMENT_ID_PREFIX = 'an-';

// A child element is the parent's OWN content unless it is a structural
// element: an annotation div (an-…) or a nested content element (s-…).
// Everything else — character spans, numbering (n-…) and edit (e-…) spans,
// <br> line breaks, and inline formatting the author-side htmlTextIndexed
// copies through verbatim (<strong>, <em>, <a>, <li>, tables) — renders before
// nested annotation children under the uniform parent rule. Mirrors
// praktio-author htmlTransform.js isOwnContentNode — keep both renderers in
// lockstep.
const isOwnContentNode = (node: Element) =>
  !(
    node.id &&
    (node.id.startsWith(CONTENT_ELEMENT_ID_PREFIX) ||
      node.id.startsWith(ANNOTATION_ELEMENT_ID_PREFIX))
  );

export const insertElementLevelAdditions_DOM = (
  doc: Document,
  additions: Annotation[],
  slideVersion = SlideVersions.Base,
) => {
  const elementLevelAdditions = additions.filter((a) => a.rootId && !a.startIndex);

  if (slideVersion < SlideVersions.TreeRestructure) {
    elementLevelAdditions.forEach((a) => {
      const { rootId, text, position, styleClass, elementId } = a;
      if (rootId) {
        const el = doc.getElementById(rootId);
        const addition = doc.createElement('div');
        (text || '').split('').forEach((c, i) => {
          const span = doc.createElement('span');
          span.setAttribute('data-id', `${elementId}-${i}`);
          span.textContent = c;
          addition.appendChild(span);
        });

        if (elementId) {
          addition.id = elementId;
        }

        if (styleClass) {
          addition.classList.add(styleClass);
        }

        if (el) {
          switch (position) {
            case ElementPositions.Before:
              (el.parentNode as El).insertBefore(addition, el);
              break;
            case ElementPositions.After:
              if (el.nextSibling) {
                (el.parentNode as El).insertBefore(addition, el.nextSibling);
              } else {
                (el.parentNode as El).appendChild(addition);
              }
              break;
            case ElementPositions.FirstChild:
              if (el.firstChild) {
                el.insertBefore(addition, el.firstChild);
              } else {
                el.appendChild(addition);
              }
              break;
            case ElementPositions.LastChild:
            default:
              el.appendChild(addition);
              break;
          }
        }
      }
    });
    return;
  }

  // Two-pass (version 2+)
  elementLevelAdditions.forEach((a) => {
    const { rootId, position, styleClass, elementId } = a;
    if (rootId) {
      const el = doc.getElementById(rootId);
      const addition = doc.createElement('div');

      if (elementId) {
        addition.id = elementId;
      }

      if (styleClass) {
        addition.classList.add(styleClass);
      }

      if (el) {
        switch (position) {
          case ElementPositions.Before:
            (el.parentNode as El).insertBefore(addition, el);
            break;
          case ElementPositions.After:
            if (el.nextElementSibling) {
              (el.parentNode as El).insertBefore(addition, el.nextElementSibling);
            } else {
              (el.parentNode as El).appendChild(addition);
            }
            break;
          case ElementPositions.FirstChild: {
            // Uniform parent rule: a parent's own content renders before its
            // nested annotation children — for content elements (s-*) exactly
            // as for annotation parents. Skip past own-content nodes (see
            // isOwnContentNode) so FIRST_CHILD means "first nested structural
            // child", not "mid-way through the parent's own content". Mirrors
            // praktio-author htmlTransform.js — keep both renderers in lockstep.
            let anchor = el.firstElementChild;
            while (anchor && isOwnContentNode(anchor)) {
              anchor = anchor.nextElementSibling;
            }
            if (anchor) {
              el.insertBefore(addition, anchor);
            } else {
              el.appendChild(addition);
            }
            break;
          }
          case ElementPositions.LastChild:
          default:
            el.appendChild(addition);
            break;
        }
      }
    }
  });

  elementLevelAdditions.forEach((a) => {
    const { text, elementId } = a;
    const el = doc.getElementById(elementId as string);
    if (!el) return;
    const fragment = doc.createDocumentFragment();
    (text || '').split('').forEach((c, i) => {
      const span = doc.createElement('span');
      span.setAttribute('data-id', `${elementId}-${i}`);
      span.textContent = c;
      fragment.appendChild(span);
    });
    el.prepend(fragment);
  });
};

export const isContentResource = (resource: Resource) => {
  const { type } = resource || {};
  return (
    type === ResourceTypes.Document ||
    type === ResourceTypes.Text ||
    isTextProvisionResource(resource)
  );
};

export const isReadOnlyContentResource = (resource: Resource) => {
  const { type, unlocked } = resource || {};
  return type === ResourceTypes.Document || (type === ResourceTypes.Text && !unlocked);
};

export const isTextProvisionResource = (resource: Resource) => {
  const { type, subType } = resource || {};
  return type === ResourceItemTypes.Provision && subType === ResourceItemTypes.Text;
};

export const getSelectionIndexes = () => {
  const selection: Sel = window.getSelection() as Sel;
  const { anchorNode, extentNode } = selection || {};
  let startIndex, endIndex;
  if (anchorNode) {
    const parentNode = anchorNode.parentNode as El;
    if (parentNode && parentNode.dataset && parentNode.dataset.id) {
      startIndex = parseInt(parentNode.dataset.id);
    }
  }

  if (selection.type === 'Range' && extentNode) {
    const parentNode = extentNode.parentNode as El;
    if (parentNode && parentNode.dataset && parentNode.dataset.id) {
      endIndex = parseInt(parentNode.dataset.id);
    }
  }

  // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
  if (!isEmpty(endIndex) && !isEmpty(startIndex) && endIndex < startIndex) {
    const temp = startIndex;
    startIndex = endIndex;
    endIndex = temp;
  }

  return {
    startIndex,
    endIndex,
  };
};

const getOffsetFromBeginning = (from: El, offset: number) => {
  let node = from;
  while (node && (!node.id || (node.id !== EditorBodyId && !node.id.startsWith('an-')))) {
    node = node.parentNode as El;
  }

  const parentNode = node;
  const textNodes: El[] = [];
  const queue: El[] = [node];
  let ignoreOffset = false;
  while (queue.length) {
    const node = queue.shift() as El;
    const { classList, nodeType, childNodes, id } = node;
    if (classList && classList.contains('pa-num')) {
      if (from.parentNode && node.isSameNode(from.parentNode)) {
        ignoreOffset = true;
        break;
      }

      continue;
    }

    if (id && id.startsWith('an-') && (!from.parentNode || !node.isSameNode(parentNode))) {
      continue;
    }

    if (node.isSameNode(from)) {
      break;
    }

    if (nodeType === Node.TEXT_NODE) {
      textNodes.push(node);
    }

    if (childNodes.length > 0) {
      for (const childNode of Array.from(childNodes)) {
        queue.unshift(childNode as El);
      }
    }
  }

  return textNodes.reduce((v, t) => (t.length ? t.length + v : v), ignoreOffset ? 0 : offset);
};

const getId = (node: El) => {
  let nodeToUse: El =
    node.nodeType === Node.TEXT_NODE &&
    node.parentNode &&
    (node.parentNode as El).id !== EditorBodyId
      ? (node.parentNode as El)
      : node;

  if (
    (nodeToUse.id || '').startsWith('n-') ||
    (nodeToUse.tagName === 'SPAN' && nodeToUse.getAttribute('data-id'))
  ) {
    nodeToUse = nodeToUse.parentNode as El;
  }

  return (nodeToUse || {}).id;
};

const getParents = (node: El) => {
  const parents = [];
  let parent = node;
  while (parent.parentNode) {
    parent = parent.parentNode as El;
    parents.push(parent);
  }

  return parents;
};

const getCommonAncestor = (a: El, b: El) => {
  const aParents = getParents(a);
  const bParents = getParents(b);
  for (let i = 0; i < aParents.length; i++) {
    const aParent = aParents[i];
    for (let j = 0; j < bParents.length; j++) {
      const bParent = bParents[j];
      if (aParent.isSameNode(bParent)) {
        return aParent;
      }
    }
  }
};

const getIndex = (nodes: El[], node: El) => {
  for (let i = 0; i < nodes.length; i++) {
    if (node.isSameNode(nodes[i])) {
      return i;
    }
  }

  return -1;
};

const getDescendants = (descendants: El[], node: El) => {
  const childNodes = node.childNodes;
  if (childNodes) {
    for (let i = 0; i < childNodes.length; i++) {
      const child = childNodes[i] as El;
      descendants.push(child);
      getDescendants(descendants, child);
    }
  }
};

export const getSelectionNodes = (target: El) => {
  const selection = window.getSelection() as Sel;
  const { anchorNode, extentNode, anchorOffset, extentOffset, type } = selection || {};
  let start = anchorNode as El;
  let end = extentNode as El;

  const startParents = getParents(start);
  const endParents = getParents(end);
  if (
    startParents.findIndex((p) => p.id === EditorBodyId) < 0 ||
    endParents.findIndex((p) => p.id === EditorBodyId) < 0
  ) {
    return null;
  }

  let startOffset = anchorOffset;
  let endOffset = extentOffset;
  // Flip if selection was reversed
  if (start === end && endOffset < startOffset) {
    start = extentNode as El;
    end = anchorNode as El;
    startOffset = extentOffset;
    endOffset = anchorOffset;
  } else {
    const commonAncestor = getCommonAncestor(start, end);
    const descendants: El[] = [];
    if (commonAncestor) {
      getDescendants(descendants, commonAncestor);
    }
    const aIndex = getIndex(descendants, start);
    const bIndex = getIndex(descendants, end);
    if (aIndex > bIndex) {
      start = extentNode as El;
      end = anchorNode as El;
      startOffset = extentOffset;
      endOffset = anchorOffset;
    }
  }

  if (type === 'Caret' && target) {
    start = target;
    end = target;
  }

  const startId = getId(start);
  const endId = getId(end);

  const startIndex = getOffsetFromBeginning(start, startOffset);
  const endIndex = getOffsetFromBeginning(end, endOffset);
  const data = {
    startId,
    startOffset,
    startIndex,
    endId,
    endOffset,
    endIndex: startIndex === endIndex ? endIndex : endIndex - 1,
    startPrevSiblingId: ((start.previousSibling as El) || {}).id,
    endPrevSiblingId: ((end.previousSibling as El) || {}).id,
  };

  return data;
};

export const createAnnotation = ({
  annotationType,
  startIndex,
  endIndex,
  panelId,
  resourceId,
  toggleModal,
  updateAnnotation,
}: {
  annotationType: string;
  startIndex: number;
  endIndex: number;
  panelId: string;
  resourceId: string;
  toggleModal: Function;
  updateAnnotation: Function;
}) => {
  const annotation = {
    type: ResourceItemTypes.Annotation,
    subType: annotationType,
    startIndex,
    endIndex,
    _id: uuidv4(),
    isNew: true,
    name: annotationType,
    panelId,
    resourceId,
  };

  if (
    annotationType === AnnotationTypes.Addition ||
    annotationType === AnnotationTypes.Addition_v2
  ) {
    toggleModal(true, { resource: annotation });
  } else {
    updateAnnotation(annotation);
  }
};

export const insertNumbering = (content: string, outlineTemplate?: OutlineTemplate) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  insertNumbering_DOM(doc, outlineTemplate);
  return doc.body.innerHTML;
};

export const stripNumbering = (content: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  stripNumbering_DOM(doc);
  return doc.body.innerHTML;
};

export const stripNumbering_DOM = (doc: Document) => {
  const numberedNodes = doc.querySelectorAll('.pa-num');
  Array.from(numberedNodes).forEach((n) => {
    n.remove();
  });
};

export const reinsertNumbering_DOM = (doc: Document, outlineTemplate?: OutlineTemplate) => {
  stripNumbering_DOM(doc);
  insertNumbering_DOM(doc, outlineTemplate);
};

export const applyEdit = ({
  annotationType,
  resourceId,
  indexedContent,
  edits,
  startIndex,
  endIndex,
  addText,
  onChange,
}: {
  annotationType: string;
  resourceId: string;
  indexedContent: string;
  edits: Annotation[];
  startIndex: number;
  endIndex: number;
  addText: string;
  onChange: Function;
}) => {
  if (startIndex) {
    const annotation = {
      type: ResourceItemTypes.Annotation,
      subType: annotationType,
      startIndex,
      endIndex: endIndex || startIndex,
      _id: uuidv4(),
      resourceId,
      text: addText,
    };

    let ignore = false;
    if (annotationType === AnnotationTypes.Delete_v2) {
      const deletes = edits.filter((e) => e.subType === AnnotationTypes.Delete_v2);
      deletes.forEach((d) => {
        if (!ignore && !d.ignore && !isEmpty(d.startIndex) && !isEmpty(d.endIndex)) {
          // Existing delete within new, ignore existing
          // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
          if (d.startIndex >= startIndex && d.endIndex <= endIndex) {
            d.ignore = true;
          }
          // New delete within existing, ignore new
          // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
          else if (startIndex >= d.startIndex && endIndex <= d.endIndex) {
            ignore = true;
          }
          // New delete starts within existing, shift new to be past existing
          else if (
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            d.startIndex <= startIndex &&
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            d.endIndex >= startIndex &&
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            d.endIndex < endIndex
          ) {
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            annotation.startIndex = d.endIndex + 1;
          }
          // Existing delete starts within existing, shift existing to be past new
          else if (
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            startIndex <= d.startIndex &&
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            endIndex >= d.startIndex &&
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            endIndex < d.endIndex
          ) {
            d.startIndex = endIndex + 1;
          }
        }
      });
    }

    if (!ignore) {
      const result = modifyAnnotation({
        indexedContent,
        annotations: edits.filter((e) => !e.ignore),
        annotation,
      });

      if (result) {
        const annotatedContent = addAnnotationsToContent(result.indexedContent, result.annotations);

        if (onChange) {
          onChange({
            edits: result.annotations,
            annotatedContent,
          });
        }

        return {
          startIndex: undefined,
          endIndex: undefined,
          addText: '',
          edits: result.annotations,
          modifiedContent: result.indexedContent,
          annotatedContent,
        };
      }
    }
  }
};

/**
 * Check if slide is of type 'Click Best Spot'
 * @param slideType - The type of the slide
 * @returns {boolean} - True if slide is of type 'Click Best Spot'
 */
export const isCBSSlide = (slideType?: string) => {
  return slideType === TemplateTypes.ClickBestSpot;
};

export const isExerciseSlide = (slideType: string) => {
  return [
    TemplateTypes.CheckAllApply,
    TemplateTypes.ChooseBestProvision,
    TemplateTypes.ClickBestSpot,
  ].includes(slideType);
};

export const isExplainerSlide = (slideType: string | undefined) => {
  return slideType === TemplateTypes.Explanation;
};

export const generateStyleId = () => `s-${nanoid(10)}`;

export const generateEditId = () => `e-${nanoid(10)}`;

export const generateAnnotationId = () => `an-${nanoid(10)}`;

export const generateId = (size: number) => nanoid(size || 10);

export const isLayeredAnnotation = (annotation: Annotation) => {
  const { rootId, startIndex, endIndex } = annotation;
  return rootId && rootId.startsWith('an-') && (startIndex || startIndex === 0) && endIndex;
};

export const isBaseAnnotation = (annotation: Annotation) => {
  const { rootId, startIndex, endIndex } = annotation;
  return (
    !rootId || (rootId && !rootId.startsWith('an-') && (startIndex || startIndex === 0) && endIndex)
  );
};

const nonceLength = 3;
export const nonce = nanoid(nonceLength);

export const hashId = (id: string) => {
  const hash = btoa(`${nonce}${id}${nonce}`);
  return hash;
};

export const unhashId = (id: string) => {
  const unhashed = atob(id);
  return unhashed.substr(nonceLength, unhashed.length - nonceLength * 2);
};

const lowerLetter = (number: number) => String.fromCharCode(number + 97);
const upperLetter = (number: number) => String.fromCharCode(number + 65);
const lowerRoman = (number: number) => romanize(number + 1).toLowerCase();
const upperRoman = (number: number) => romanize(number + 1);
const twoDigitSection = (parentNumber: number | undefined, number: number) =>
  `${parentNumber}.${number + 1 < 10 ? '0' + (number + 1) : number + 1}`;

const lowerCaseLetter = (number: number) => {
  return `${lowerLetter(number)}. `;
};

const lowerCaseRoman = (number: number) => {
  return `${lowerRoman(number)}. `;
};

const upperCaseLetter = (number: number) => {
  return `${upperLetter(number)}. `;
};

const upperCaseRoman = (number: number) => {
  return `${upperRoman(number)}. `;
};

const articleRoman = (number: number) => {
  return `Article ${upperRoman(number)} `;
};

const twoDigitSectionParent = (parentNumber: number | undefined, number: number) => {
  return `Section ${twoDigitSection(parentNumber, number)} `;
};

const lowerCaseLetterParenthesis = (number: number) => {
  return `(${lowerLetter(number)}) `;
};

const lowerCaseRomanParenthesis = (number: number) => {
  return `(${lowerRoman(number)}) `;
};

const upperCaseLetterParenthesis = (number: number) => {
  return `(${upperLetter(number)}) `;
};

const upperCaseRomanParenthesis = (number: number) => {
  return `(${upperRoman(number)}) `;
};

const updateOutline = ({
  doc,
  el,
  decrease,
  showMarkup,
  oldParentNumber,
  newParentNumber,
  parentOnly,
}: {
  doc: Document;
  el: El;
  decrease: boolean | undefined;
  showMarkup: boolean;
  oldParentNumber?: number;
  newParentNumber?: number;
  parentOnly?: boolean;
}) => {
  const originalNumber = parseInt(el.dataset.newNum || el.dataset.num, 10);
  const adjustedNumber = parseInt(el.dataset.adjNum || el.dataset.num, 10);
  const outlineType = el.dataset.template;
  const level = parseInt(el.dataset.newLevel || el.dataset.level, 10);
  const parentNumber = parseInt(el.dataset.newParent || el.dataset.parent, 10);
  let oldNumber = parentOnly ? originalNumber : decrease ? originalNumber : originalNumber - 1;
  let newNumber = parentOnly ? adjustedNumber : decrease ? adjustedNumber - 1 : adjustedNumber;
  let oldProcessed: number | string = oldNumber + 1;
  let newProcessed: number | string = newNumber + 1;
  if (outlineType) {
    switch (outlineType) {
      case OutlineTypes.LowerLetter:
      case OutlineTypes.LowerLetterParen:
        oldProcessed = lowerLetter(oldNumber);
        newProcessed = lowerLetter(newNumber);
        break;
      case OutlineTypes.LowerRoman:
      case OutlineTypes.LowerRomanParen:
        oldProcessed = lowerRoman(oldNumber);
        newProcessed = lowerRoman(newNumber);
        break;
      case OutlineTypes.UpperLetter:
      case OutlineTypes.UpperLetterParen:
        oldProcessed = upperLetter(oldNumber);
        newProcessed = upperLetter(newNumber);
        break;
      case OutlineTypes.ArticleRoman:
      case OutlineTypes.UpperRoman:
      case OutlineTypes.UpperRomanParen:
        oldProcessed = upperRoman(oldNumber);
        newProcessed = upperRoman(newNumber);
        break;
      case OutlineTypes.TwoDigitSection:
        oldProcessed = twoDigitSection(oldParentNumber || parentNumber, oldNumber);
        newProcessed = twoDigitSection(newParentNumber || parentNumber, newNumber);
        break;
      case OutlineTypes.Number:
      case OutlineTypes.NumberParen:
      default:
        break;
    }
  } else {
    switch (level) {
      case 1:
        oldProcessed = lowerLetter(oldNumber);
        newProcessed = lowerLetter(newNumber);
        break;
      case 2:
        oldProcessed = lowerRoman(oldNumber);
        newProcessed = lowerRoman(newNumber);
        break;
      case 3:
        oldProcessed = upperLetter(oldNumber);
        newProcessed = upperLetter(newNumber);
        break;
      default:
        break;
    }
  }

  el.textContent = null;
  if ([OutlineTypes.ArticleRoman, OutlineTypes.TwoDigitSection].includes(outlineType)) {
    const child = doc.createElement('span');
    child.textContent = outlineType === OutlineTypes.ArticleRoman ? 'Article ' : 'Section ';
    el.appendChild(child);
  } else if (
    [
      OutlineTypes.LowerLetterParen,
      OutlineTypes.LowerRomanParen,
      OutlineTypes.UpperLetterParen,
      OutlineTypes.UpperRomanParen,
      OutlineTypes.NumberParen,
    ].includes(outlineType)
  ) {
    oldProcessed = `(${oldProcessed})`;
    newProcessed = `(${newProcessed}) `;
  } else {
    oldProcessed = `${oldProcessed}.`;
    newProcessed = `${newProcessed}. `;
  }

  const newNum = doc.createElement('span');
  if (showMarkup) {
    const oldNum = doc.createElement('span');
    oldNum.classList.add('is-annotation', 'Delete');
    newNum.classList.add('is-annotation', 'Addition_v2');
    oldNum.textContent = oldProcessed.toString();
    el.appendChild(oldNum);
    el.classList.add('marked');
  }

  newNum.textContent = newProcessed.toString();
  el.appendChild(newNum);

  if (!showMarkup) {
    el.dataset.newNum = newNumber.toString();
  }
  el.dataset.adjNum = newNumber.toString();

  if (outlineType === OutlineTypes.ArticleRoman && el.parentNode) {
    const childSections = el.parentNode.querySelectorAll(':scope > div .pa-num.pa-tds');
    childSections.forEach((s) =>
      updateOutline({
        doc,
        el: s as El,
        decrease,
        showMarkup,
        oldParentNumber: oldNumber + 1,
        newParentNumber: newNumber + 1,
        parentOnly: true,
      }),
    );
  }
};

const getNumber = ({
  outlineType,
  number,
  level,
  parentNumber,
}: {
  outlineType: string;
  number: number;
  level: number;
  parentNumber?: number;
}) => {
  let processedNumber: number | string = number;
  if (outlineType) {
    switch (outlineType) {
      case OutlineTypes.LowerLetter:
        processedNumber = lowerCaseLetter(number);
        break;
      case OutlineTypes.LowerRoman:
        processedNumber = lowerCaseRoman(number);
        break;
      case OutlineTypes.UpperLetter:
        processedNumber = upperCaseLetter(number);
        break;
      case OutlineTypes.UpperRoman:
        processedNumber = upperCaseRoman(number);
        break;
      case OutlineTypes.ArticleRoman:
        processedNumber = articleRoman(number);
        break;
      case OutlineTypes.TwoDigitSection:
        processedNumber = twoDigitSectionParent(parentNumber, number);
        break;
      case OutlineTypes.LowerLetterParen:
        processedNumber = lowerCaseLetterParenthesis(number);
        break;
      case OutlineTypes.LowerRomanParen:
        processedNumber = lowerCaseRomanParenthesis(number);
        break;
      case OutlineTypes.UpperLetterParen:
        processedNumber = upperCaseLetterParenthesis(number);
        break;
      case OutlineTypes.UpperRomanParen:
        processedNumber = upperCaseRomanParenthesis(number);
        break;
      case OutlineTypes.NumberParen:
        processedNumber = `(${number + 1}) `;
        break;
      case OutlineTypes.Number:
      default:
        processedNumber = `${number + 1}. `;
        break;
    }
  } else {
    switch (level) {
      case 1:
        processedNumber = lowerCaseLetter(number);
        break;
      case 2:
        processedNumber = lowerCaseRoman(number);
        break;
      case 3:
        processedNumber = upperCaseLetter(number);
        break;
      default:
        processedNumber = `${number + 1}. `;
        break;
    }
  }

  return processedNumber;
};

const getOutlineClass = ({ outlineType, level }: { outlineType: string; level: number }) => {
  let className = '';
  if (outlineType) {
    switch (outlineType) {
      case OutlineTypes.LowerLetter:
        className = 'pa-lcl';
        break;
      case OutlineTypes.LowerRoman:
        className = 'pa-lcr';
        break;
      case OutlineTypes.UpperLetter:
        className = 'pa-ucl';
        break;
      case OutlineTypes.UpperRoman:
        className = 'pa-ucr';
        break;
      case OutlineTypes.ArticleRoman:
        className = 'pa-ar';
        break;
      case OutlineTypes.TwoDigitSection:
        className = 'pa-tds';
        break;
      case OutlineTypes.LowerLetterParen:
        className = 'pa-lclp';
        break;
      case OutlineTypes.LowerRomanParen:
        className = 'pa-lcrp';
        break;
      case OutlineTypes.UpperLetterParen:
        className = 'pa-uclp';
        break;
      case OutlineTypes.UpperRomanParen:
        className = 'pa-ucrp';
        break;
      case OutlineTypes.NumberParen:
        className = 'pa-np';
        break;
      case OutlineTypes.Number:
      default:
        className = 'pa-n';
        break;
    }
  } else {
    switch (level) {
      case 1:
        className = 'pa-lcl';
        break;
      case 2:
        className = 'pa-lcr';
        break;
      case 3:
        className = 'pa-ucl';
        break;
      default:
        className = 'pa-n';
        break;
    }
  }

  return className;
};

const insertNumberingRecursive = (
  doc: Document,
  node: El,
  level: number,
  outlineTemplate: OutlineTemplate,
  parentNumber?: number,
) => {
  const template = outlineTemplate[level];
  const nodesToNumber = Array.isArray(node)
    ? node
    : node.querySelectorAll(
        `:scope > ${!level ? '.pa-contract-article' : '.pa-contract-numsection'}`,
      );

  Array.from(nodesToNumber).forEach((n, i) => {
    let num = n.querySelector(':scope > .pa-num');
    if (!num) {
      num = doc.createElement('span');
      num.classList.add('pa-num');
      num.id = `n-${nanoid(5)}`;
      num.dataset.num = i;
      num.dataset.level = level;

      if (template) {
        num.dataset.template = template;
        if (template === OutlineTypes.TwoDigitSection) {
          num.dataset.parent = parentNumber;
        }
      }

      n.prepend(num);
    }

    const number = getNumber({
      outlineType: template,
      number: i,
      level,
      parentNumber,
    });

    const className = getOutlineClass({
      outlineType: template,
      level,
    });

    num.textContent = number;
    num.classList.add(className);
    insertNumberingRecursive(doc, n, level + 1, outlineTemplate, i + 1);
  });
};

const insertNumbering_DOM = (doc: Document, outlineTemplate?: OutlineTemplate) => {
  const { body } = doc;
  const segments: (El | El[])[] = [];
  let chunk: El[] | null;
  body.childNodes.forEach((child) => {
    const c = child as El;
    if (c.classList) {
      if (c.classList.contains('pa-contract-segment')) {
        segments.push(c);
        chunk = null;
      } else if (c.classList.contains('pa-contract-article')) {
        if (!chunk) {
          chunk = [];
          segments.push(chunk);
        }
        chunk.push(c);
      }
    }
  });

  segments.forEach((s) => {
    insertNumberingRecursive(doc, s as El, 0, outlineTemplate || {});
  });
};

export const getCurrentSlide = (state: RootState): Slide => {
  return (
    (state.slidesLitmos && state.slidesLitmos.currentSlide) ||
    ({ panels: [] as Panel[], timeline: [] as Annotation[] } as Slide)
  );
};

export const getDefaultInstructions = (course?: Course, slide?: Slide) => {
  let instructions;
  switch ((slide || {}).type) {
    case TemplateTypes.CheckAllApply:
      if ((slide || {}).isSingle) {
        instructions = course?.allApplySingleInstructions;
        break;
      }

      instructions = course?.allApplyInstructions;
      break;
    case TemplateTypes.ClickBestSpot:
      instructions = course?.bestSpotInstructions;
      break;
    case TemplateTypes.ChooseBestProvision:
      instructions = course?.bestProvisionInstructions;
      break;
    default:
      break;
  }

  return slide?.defaultInstructions || instructions;
};

export const isDynamicNameAnnotation = (annotation: Annotation) => {
  return (
    annotation.subType &&
    [
      AnnotationTypes.Highlight,
      AnnotationTypes.Strikethrough,
      AnnotationTypes.Focus,
      AnnotationTypes.Tooltip,
      AnnotationTypes.Correct,
      AnnotationTypes.Incorrect,
      AnnotationTypes.Delete,
      AnnotationTypes.Delete_v2,
      AnnotationTypes.Dynamic,
      AnnotationTypes.AnswerBase,
      AnnotationTypes.Choice,
    ].includes(annotation.subType)
  );
};

export const processAdditions = (
  annotations: Annotation[],
  contentResource: Annotation,
  slideVersion = SlideVersions.Base,
) => {
  if (contentResource && annotations) {
    const domParser = new DOMParser();
    const doc = domParser.parseFromString(
      contentResource.originalIndexedContent || '',
      'text/html',
    );
    return processAdditions_DOM(doc, annotations, contentResource, slideVersion);
  } else {
    contentResource.indexedContent = contentResource.originalIndexedContent;
  }

  return false;
};

export const processAdditions_DOM = (
  doc: Document,
  annotations: Annotation[],
  contentResource: Annotation,
  slideVersion = SlideVersions.Base,
) => {
  if (contentResource) {
    const { panelId, version, outlineTemplate } = contentResource;
    let filteredAdditions: Annotation[] = [];
    (annotations || []).forEach((a) => {
      if (a.panelId === panelId || !panelId) {
        if (a.subType === AnnotationTypes.Addition || a.subType === AnnotationTypes.Addition_v2) {
          filteredAdditions.push(a);
          if (!isEmpty(a.startIndex) && a.text) {
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            a.endIndex = a.startIndex + (a.text.length === 0 ? 0 : a.text.length - 1);
          }
        }
      }
    });

    filteredAdditions = sortBy(filteredAdditions, 'startIndex');
    // Recalculate endIndexes - typically just text length + startIndex,
    // but if Additions are within Additions, we need to account for those
    for (let i = 0; i < filteredAdditions.length; i++) {
      const annotation = filteredAdditions[i];
      if (annotation.startIndex || annotation.startIndex === 0) {
        let endIndex = annotation.endIndex;
        if (!isEmpty(endIndex)) {
          for (let j = i + 1; j < filteredAdditions.length; j++) {
            const followingAnnotation = filteredAdditions[j];
            const { text, startIndex } = followingAnnotation;
            const offset = text ? text.length : 0;
            // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
            if (offset && !isEmpty(startIndex) && startIndex <= endIndex) {
              // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
              endIndex += offset;
              annotation.endIndex = endIndex;
            }
          }
        }
      }
    }

    insertCharacterLevelAdditions_DOM(doc, filteredAdditions, false);
    insertElementLevelAdditions_DOM(doc, filteredAdditions, slideVersion);
    insertCharacterLevelAdditions_DOM(doc, filteredAdditions, true);
    if (version === 2) {
      reinsertNumbering_DOM(doc, outlineTemplate);
    }

    contentResource.indexedContent = doc.body.innerHTML;
    return true;
  }

  return false;
};

export const generateDynamicAnnotationName = (
  annotation: Annotation,
  contentResource: Annotation,
) => {
  if (contentResource.indexedContent) {
    const content = getContentForAnnotation(annotation, contentResource.indexedContent);

    if (content) {
      let name = content.substring(0, 30);
      if (!name || (name && name.trim() === '')) {
        name = `${annotation.type}, ${annotation.subType}`;
      }

      return name;
    }
  }
};

export const generateDynamicAnnotationName_DOM = (doc: Document, annotation: Annotation) => {
  const content = getContentForAnnotation_DOM(doc, annotation);
  if (content) {
    let name = content.substring(0, 30);
    if (!name || (name && name.trim() === '')) {
      name = `${annotation.type}, ${annotation.subType}`;
    }

    return name;
  }
};

export const processResource = (r: Annotation, resources: Annotation[]) => {
  if (resources) {
    const resource = resources.find((rr) => rr._id === r.resourceId);
    if (resource && r.type) {
      // Update resource content if type is Document
      // from base Doc resource
      if ([ResourceTypes.Document].includes(r.type) && resources) {
        r.name = resource.name;
        r.content = resource.content;
        r.indexedContent = resource.indexedContent;
        r.version = resource.version;
        if (resource.version === 2) {
          if (resource.content) {
            r.indexedContent = htmlTextIndexed(resource.content);
          }
          r.isLandscape = !!resource.isLandscape;
          r.baseClass = resource.baseClass;
          r.outlineTemplate = resource.outlineTemplate;
        }
        r.originalIndexedContent = r.indexedContent;
      } else if (r.type === ResourceTypes.Stateful) {
        r.files = resource.files;
        r.active = resource.active;
        r.name = resource.name;
      }
    }
  }
};

export const processAssets = (slide: Slide, resources: Annotation[], isLive: boolean) => {
  const { timeline: assets, type: slideType, subType: slideSubType } = slide;
  let snippets: Annotation[] = [];
  let contentResources: Annotation[] = [];
  const dynamicNameAnnotations: Annotation[] = [];
  const additions: Annotation[] = [];
  assets.forEach((a) => {
    processResource(a, resources);
    if (a.subType === AnnotationTypes.Snippet) {
      snippets.push(a);
    } else if (a.type === ResourceTypes.Document || a.type === ResourceTypes.Text) {
      if (a.type === ResourceTypes.Text) {
        if (a.content) {
          a.indexedContent = htmlTextIndexed(a.content);
          a.originalIndexedContent = a.indexedContent;
        }
        a.unlocked = true;
      }
      contentResources.push(a);
    } else if (a.subType === AnnotationTypes.Addition) {
      const length = (a.text || '').length;
      if (!isEmpty(a.startIndex)) {
        // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
        a.endIndex = a.startIndex + (length ? length - 1 : 0);
      }
      additions.push(a);
    } else if (a.subType === AnnotationTypes.Addition_v2) {
      additions.push(a);
    } else if (!isLive && isDynamicNameAnnotation(a)) {
      dynamicNameAnnotations.push(a);
    }

    // Set all resources to active if slide is not Explanation
    // and the resource is not a Correct Annotation
    if (slideType !== TemplateTypes.Explanation && a.subType !== AnnotationTypes.Correct) {
      a.active = true;
    }
  });

  contentResources.forEach((r) => {
    const domParser = new DOMParser();
    if (r.originalIndexedContent) {
      const doc = domParser.parseFromString(r.originalIndexedContent, 'text/html');

      if (r.version === 2) {
        insertNumbering_DOM(doc, r.outlineTemplate);
        r.originalIndexedContent = doc.body.innerHTML;
        if (r.content) {
          r.content = insertNumbering(r.content, r.outlineTemplate);
        }
      }

      processAdditions_DOM(doc, additions, r, slide.version);

      // Iterate through any dynamically named annotations to generate names
      // from indexedContent
      dynamicNameAnnotations
        .filter((dr) => dr.panelId === r.panelId)
        .forEach((a, i) => {
          a.name = generateDynamicAnnotationName_DOM(doc, a);
        });

      r.indexedContent = doc.body.innerHTML;

      const snippet = snippets.find((sr) => sr.panelId === r.panelId);
      // Check for Snippet Annotation
      // and if it exists, apply the Snippet to the content
      if (snippet && !isEmpty(snippet.startIndex) && !isEmpty(snippet.endIndex)) {
        // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
        r.snippedContent = extractSnippetContent_DOM(doc, snippet.startIndex, snippet.endIndex);
      }
    }

    if (slideSubType === CataTypes.EditHorizontal || slideSubType === CataTypes.EditVertical) {
      processAnswerEdits({
        assets: assets || [],
        resource: r || {},
        slideType,
        slideVersion: slide.version || SlideVersions.Base,
      });
    }
  });

  additions.forEach((a) => {
    a.name = a.text?.substring(0, 30);
  });

  // Resolve imageUrl for Answer items with imageId (CATA image swap)
  assets
    .filter((a) => a.type === ResourceItemTypes.Answer && a.imageId)
    .forEach((a) => {
      const imageResource = resources.find((r) => r._id === a.imageId);

      if (imageResource) {
        a.imageUrl = imageResource.url;
      }
    });
};

const processAnswerEdits = ({
  assets,
  resource,
  slideType,
  slideVersion,
}: {
  assets: Annotation[];
  resource: Annotation;
  slideType: string;
  slideVersion: number;
}) => {
  const { panelId, indexedContent, snippedContent, content } = resource;
  const answerBase = assets.find(
    (a) => a.subType === AnnotationTypes.AnswerBase && a.panelId === panelId,
  );

  // If there's an AnswerBase on the panelId of the ContentResource,
  // then this is the resource to use for processing content
  // for CATA Best Edit Answers
  if (answerBase) {
    const AnnotationEnabledTypes = [
      TemplateTypes.Explanation,
      TemplateTypes.ClickBestSpot,
      TemplateTypes.CheckAllApply,
    ];

    const resourceContent = snippedContent || indexedContent || content;
    const filteredAnswers = (assets || []).filter((a) => a.type === ResourceItemTypes.Answer);

    filteredAnswers.forEach((a) => {
      const contentObject = {
        type: ResourceTypes.Document,
        panelId,
        originalIndexedContent: resourceContent,
        indexedContent: resourceContent,
      };

      const panelAssetsNoAddition: Annotation[] = [];
      const additions: Annotation[] = [];
      assets.forEach((pa) => {
        if (pa.panelId === panelId) {
          if (pa.subType !== AnnotationTypes.Addition_v2) {
            panelAssetsNoAddition.push(cloneDeep(pa));
          } else {
            additions.push(cloneDeep(pa));
          }
        }
      });

      // Sort edits before adding them
      const edits = sortBy(a.answerEdits, 'startIndex');
      let offset = 0;
      edits.forEach((e) => {
        const edit = { ...e, panelId, active: true, isAnswerEdit: true };
        processAssetWithExisting({
          asset: edit as any,
          assets: panelAssetsNoAddition as any,
          contentResource: contentObject,
          processAdditions: ((annotations: Annotation[], contentResource: Annotation) =>
            processAdditions(annotations, contentResource, slideVersion)) as any,
        });

        if (e.subType === AnnotationTypes.Addition_v2) {
          offset += (e.text || '').length;
        }
      });

      additions.forEach((ad) => {
        if (
          !isEmpty(ad.startIndex) &&
          !isEmpty(answerBase.startIndex) &&
          // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
          ad.startIndex >= answerBase.startIndex
        ) {
          // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
          ad.startIndex += offset;
          ad.endIndex = (ad.endIndex || 0) + offset;
        }
      });

      const processedAnnotations = [...panelAssetsNoAddition, ...additions];
      a.processedContent = AnnotationEnabledTypes.includes(slideType)
        ? resource.version === 2
          ? processDocument_v2({
              content: contentObject.indexedContent || '',
              annotations: [...panelAssetsNoAddition, ...additions],
              slideType,
              isLive: true,
            })
          : addAnnotationsToContent(
              contentObject.indexedContent || '',
              panelAssetsNoAddition,
              slideType,
              true,
            )
        : contentObject.indexedContent;

      a.processedContentFeedback = AnnotationEnabledTypes.includes(slideType)
        ? resource.version === 2
          ? processDocument_v2({
              content: contentObject.indexedContent || '',
              annotations: [...panelAssetsNoAddition, ...additions],
              slideType,
              isLive: true,
              outOfTries: true,
            })
          : addAnnotationsToContent(
              contentObject.indexedContent || '',
              panelAssetsNoAddition,
              slideType,
              true,
            )
        : contentObject.indexedContent;

      // Build Edit Set Preview
      const { startIndex, endIndex } = answerBase;
      let answerContent = '';
      if (a.processedContent && (startIndex || startIndex === 0)) {
        const keys: {
          [key: string]: any;
        } = [];

        for (let i = startIndex; i <= (endIndex || startIndex) + offset; i++) {
          keys[i?.toString()] = true;
        }

        const domParser = new DOMParser();
        const doc = domParser.parseFromString(a.processedContent, 'text/html');
        const spans = doc.querySelectorAll('span[data-id]');
        spans.forEach((span) => {
          const dataId = span.getAttribute('data-id');
          if (dataId && keys[dataId]) {
            answerContent += span.outerHTML;
          }
        });

        // Add the AnswerBase Annotation back in, but with the calculated offset
        processedAnnotations.push({ ...answerBase, endIndex: (endIndex || 0) + offset });
      }

      a.annotatedEdits = answerContent;
      a.processedAnnotations = processedAnnotations;
    });
  }
};

/**
 * Updates provided slide in place with resources retrieved from service
 * @param {*} slide
 * @param {*} isLive
 */
/*export const loadSlideResources = async ({
  slide,
  isLive,
}: {
  slide: Slide;
  isLive: boolean;
}) => {
  console.log('Getting slide resources...');
  const allResourceIds: string[] = [];
  // Get all resources defined in the resource list
  const assets = slide.timeline;
  if (assets) {
    assets
      .filter((a) => a.resourceId)
      .forEach((a) => {
        allResourceIds.push(a.resourceId as string);
      });
  }

  try {
    // Get all resources
    const resources = [];
    const uniqueIds = uniq(allResourceIds);
    for (let id of uniqueIds) {
      const response = await getResource(id);
      const resource = response.data;
      resources.push(resource);
    }

    if (slide.audioId) {
      const response = await getResource(slide.audioId);
      slide.audio = response.data;
    }

    if (slide.referenceContentId) {
      const response = await getResource(slide.referenceContentId);
      slide.referenceContent = response.data;
    }

    processAssets(slide, resources, isLive);
    if (slide.panels.length === 3) {
      const id = slide.panels[1]._id;
      const panelContainsDocument = slide.timeline.find(
        (r) => r.panelId === id && r.type === ResourceTypes.Document
      );

      slide.panels[0].width = 0.32;
      slide.panels[1].width = panelContainsDocument ? 0.4 : 0.28;
      slide.panels[2].width = panelContainsDocument ? 0.28 : 0.4;
    }

    return slide;
  } catch (e) {
    console.error(e);
  }
};*/

const validResourceTypes = Object.values(ResourceTypes).filter((r) => r !== ResourceTypes.Audio);

export const findBaseResource = (panelTimeline: Annotation[]) => {
  if (panelTimeline) {
    const resource = panelTimeline.find((r) => r.type && validResourceTypes.includes(r.type));
    if (resource) {
      return resource;
    }
  }
  return undefined;
};

export const getAdpativeSortValue = (slide: Slide) => {
  switch (slide.adaptive) {
    case AdaptiveTypes.immediate:
      return 1;
    case AdaptiveTypes.queued:
      return 2;
    case AdaptiveTypes.primary:
    default:
      return 0;
  }
};

export const orderSlides = (slides: Slide[]) => {
  const orderedSlides: Slide[] = [];
  if (slides && slides.length > 0) {
    const mainSlides: Slide[] = [];
    const taggedSlides: { [index: string]: Slide[] } = {};
    slides.forEach((s) => {
      if (
        ![TemplateTypes.Explanation, TemplateTypes.Conclusions].includes(s.type) &&
        s.adaptive &&
        s.tags &&
        s.tags.length > 0 &&
        s.active &&
        s.adaptive !== AdaptiveTypes.notAdaptive
      ) {
        const tagId = s.tags[0]._id as string;
        if (!taggedSlides[tagId]) {
          taggedSlides[tagId] = [];
        }

        taggedSlides[tagId].push(s);
      } else {
        mainSlides.push(s);
      }
    });

    Object.keys(taggedSlides).forEach((k) => {
      let primary = taggedSlides[k].find((s) => s.adaptive === AdaptiveTypes.primary);
      // If primary doesn't exist, we need a dummy slide to show the row properly
      if (!primary) {
        const taggedSlide = taggedSlides[k].find((s) => s._id);
        primary = {
          ...taggedSlide,
          name: 'Primary Placeholder',
          adaptive: AdaptiveTypes.primary,
          _id: 'primary_placeholder',
          isPlaceholder: true,
        } as Slide;
        taggedSlides[k].push(primary);
      }

      mainSlides.push(primary);
    });

    mainSlides.sort((a, b) =>
      b.type === TemplateTypes.Conclusions
        ? -1
        : !isEmpty(a.order) && !isEmpty(b.order)
        ? // @ts-expect-error: ignore 'undefined' check (verified through isEmpty)
          a.order - b.order
        : 0,
    );

    mainSlides.forEach((s) => {
      let slideGroup = [s];
      if (
        s.adaptive &&
        s.tags &&
        s.tags.length > 0 &&
        s.active &&
        s.adaptive !== AdaptiveTypes.notAdaptive
      ) {
        slideGroup = taggedSlides[s.tags[0]._id as string];
      }
      slideGroup.sort((a, b) => {
        const aValue = getAdpativeSortValue(a);
        const bValue = getAdpativeSortValue(b);
        return aValue - bValue;
      });

      orderedSlides.push(...slideGroup);
    });
  }
  return orderedSlides;
};

export const isEndOfSlide = (player: Player) => {
  const { playedSeconds, duration } = player;

  if (!playedSeconds || !duration) {
    return false;
  }

  return playedSeconds === duration;
};

/**
 * Check if CATA slide has document edits
 * @param layout - The layout of the slide
 * @param timeline - The timeline of the slide
 * @returns {boolean} - True if slide has document edits
 */
export const cataSlideHasDocumentEdits = (
  layout: string | undefined,
  timeline: Annotation[] | undefined,
): boolean => {
  if (!layout || !timeline) return false;

  return (
    layout === CATALayouts.TRI_PANEL &&
    timeline.some(
      (item: Annotation) => item.type === ResourceItemTypes.Answer && item.annotatedEdits,
    )
  );
};
